/**
 * Call disposition wire values + visual config + localized labels.
 *
 * Wire values (`Interested`, `Not Interested`, etc.) are exact strings stored
 * in the DB and read by analytics — do NOT change them. Only the rendered
 * label is translated. See `i18n/GLOSSARY.md`.
 */
import { CheckCircle, XCircle, PhoneMissed, Clock, Ban } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface DispositionConfig {
  /** Wire value sent to the backend. Stays in English. */
  value: string
  /** i18next key under the `campaigns` namespace. */
  labelKey: string
  icon: typeof CheckCircle
  color: string
}

export const DISPOSITIONS: readonly DispositionConfig[] = [
  {
    value: 'Interested',
    labelKey: 'dispositions.interested',
    icon: CheckCircle,
    color: 'bg-success/15 text-success border-success/30 hover:bg-success/25',
  },
  {
    value: 'Not Interested',
    labelKey: 'dispositions.notInterested',
    icon: XCircle,
    color: 'bg-error/15 text-error border-error/30 hover:bg-error/25',
  },
  {
    value: 'No Answer',
    labelKey: 'dispositions.noAnswer',
    icon: PhoneMissed,
    color: 'bg-warning/15 text-warning border-warning/30 hover:bg-warning/25',
  },
  {
    value: 'Callback',
    labelKey: 'dispositions.callback',
    icon: Clock,
    color: 'bg-info/15 text-info border-info/30 hover:bg-info/25',
  },
  {
    value: 'DNC',
    labelKey: 'dispositions.dnc',
    icon: Ban,
    color: 'bg-text-muted/15 text-text-muted border-text-muted/30 hover:bg-text-muted/25',
  },
] as const

/** Returns the translated label for a disposition wire value. */
export function useDispositionLabel(value: string): string {
  const { t } = useTranslation('campaigns')
  const cfg = DISPOSITIONS.find((d) => d.value === value)
  return cfg ? t(cfg.labelKey) : value
}
